import {FC, ReactNode, useState, useEffect, useRef} from 'react'
import {useTranslation} from 'react-i18next'
import {useOnClickOutside} from 'usehooks-ts'
import SVGIcon from './SVGIcon'

export type IVisibleColumnsOption = {
    value: string
    label: string
}

export type IVisibleColumnsSelectDropdownProps = {
    options: Array<IVisibleColumnsOption>
    selected: Array<string>
    toggleOption: ICallbackFunction
    handleReset: IHandleResetFunction
}

export type ICallbackFunction = (value: string) => void
export type IHandleResetFunction = () => void

const VisibleColumnsSelectDropdown: FC<IVisibleColumnsSelectDropdownProps> = ({
    options,
    selected,
    toggleOption,
    handleReset,
}): ReactNode => {
    const minSelectedLimit: number = 3
    const maxSelectedLimit: number = 6
    const {t} = useTranslation()
    const ref = useRef(null)
    const [isOpen, setIsOpen] = useState<boolean>(false)
    const [selectedCounter, setSelectedCounter] = useState<number>(0)

    useEffect(() => {
        setSelectedCounter(selected.length)
    }, [selected])

    const handleOpen = () => {
        setIsOpen(!isOpen)
    }

    const handleSelect = (value: string) => {
        if (selected.includes(value)) {
            if (selected.length > minSelectedLimit) {
                setSelectedCounter((prevState) => prevState - 1)
                toggleOption(value)
            }
        } else {
            if (selected.length < maxSelectedLimit) {
                setSelectedCounter((prevState) => prevState + 1)
                toggleOption(value)
            }
        }
    }

    useOnClickOutside(ref, () => {
        setIsOpen(false)
    })

    return (
        <div ref={ref} className={'c-multi-select-dropdown' + (isOpen ? ' c-multi-select-dropdown__active' : '')}>
            <div className="c-multi-select-dropdown__selected-rate" onClick={handleOpen}>
                <div className="sort-rate flex">
                    <span className="rate-of-return">{t('label.dropdown.rate-of-return.title')}</span>
                    <span className="check " style={{paddingTop: '2px'}}>
                        <SVGIcon id={'check-icon-black'} className={''} width={16} height={24} fill={'#414141'} />
                    </span>
                </div>
            </div>
            <ul className="c-multi-select-dropdown__options sticky-to-right">
                <li className="c-multi-select-dropdown_rate-result flex">
                    <span className="flex">
                        {t('label.dropdown.rate-of-return.selected', {count: selectedCounter, limit: maxSelectedLimit})}
                    </span>
                    <button className="aol-btn outline-btn text-btn-blue flex" onClick={handleReset}>
                        {t('label.dropdown.rate-of-return.clean')}
                    </button>
                </li>
                {options.map((option: IVisibleColumnsOption, index: number) => {
                    return (
                        <li
                            key={'visible_column_option_idx_' + index.toString()}
                            className="c-multi-select-dropdown__option">
                            <label
                                htmlFor={'visible_column_option_idx_' + index.toString()}
                                className="flex"
                                onClick={() => handleSelect(option.value)}>
                                <input
                                    type="checkbox"
                                    checked={selected.includes(option.value)}
                                    className="c-multi-select-dropdown__option-checkbox"
                                    readOnly
                                    disabled={!selected.includes(option.value) && selectedCounter === maxSelectedLimit}
                                />
                                <span className="check-mark"></span>
                                <span className="check-label">{t(option.label)}</span>
                            </label>
                        </li>
                    )
                })}
            </ul>
        </div>
    )
}

export default VisibleColumnsSelectDropdown
